import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CalendarMonth as CalendarIcon,
  EventBusy as AbsenceIcon,
} from "@mui/icons-material";
import { GridValidRowModel } from "@mui/x-data-grid";
import { useSnackbar } from "notistack";
import { endOfYear, startOfYear } from "date-fns";
import {
  PageTitle,
  DataGrid,
  FilterBar,
  useQueryPagination,
} from "../../components";
import { useResponsive } from "../../hooks";
import { UpdateAbsenceInput } from "../../API";
import { toIntervalString, useAbsenceLib } from "../../lib";
import { columns, hiddenColumnsMobile } from "./columns";
import { useActionModals } from "./Modals";
import { absencesReportQuery } from "./utils";

const TEST_ID = "absences";

export const AbsenceTracker = () => {
  const [employeeID, setEmployeeID] = useState("");
  const [dates, setDates] = useState<Interval | null>({
    start: startOfYear(new Date()),
    end: endOfYear(new Date()),
  });

  const { isMd } = useResponsive();
  const navigate = useNavigate();
  const { showActionModal } = useActionModals();
  const { updateAbsenceRecord } = useAbsenceLib();
  const { enqueueSnackbar } = useSnackbar();
  const showSnackbar = (message: string, error = false) => {
    enqueueSnackbar(message, { variant: error ? "error" : "success" });
  };

  const { data, isError, refetch, isPending, pagination } = useQueryPagination(
    absencesReportQuery({
      dates,
      id: employeeID,
    })
  );
  const handleRowUpdate = (row: GridValidRowModel) => {
    const { description, id, endDate, startDate } = row;
    const data = {
      description,
      id,
      endDate,
      startDate,
    } as UpdateAbsenceInput;
    updateAbsenceRecord(data, {
      onSuccess: () => postAction("Time Off request has been updated.", false),
      onError: () =>
        postAction(
          "Error updating Time Off request. Please try again in a few seconds",
          true
        ),
    });
    return { ...data, id: row.id };
  };

  const postAction = (message: string, isError: boolean) => {
    showSnackbar(message, isError);
  };

  const fileName = `${
    dates ? `${toIntervalString(dates)} - ` : ""
  }${employeeID}Absence_Report`;

  return (
    <>
      <PageTitle
        action={{
          label: "View Calendar",
          icon: <CalendarIcon />,
          onClick: () => navigate("/admin/calendar"),
        }}
        icon={<AbsenceIcon />}
        title="Absence Tracker"
        testID={TEST_ID}
      />

      <FilterBar
        employee={{ value: employeeID, onChange: setEmployeeID }}
        dates={{
          value: dates,
          onChange: setDates,
          type: "range",
          inputFormat: "yyyy-MM-dd",
        }}
        testID="absence"
      />

      <DataGrid
        columns={columns({ showActionModal })}
        data={data?.items || []}
        csvOptions={{ fileName }}
        pagination={pagination}
        emptyState={{
          title: "Time Off Requests",
          description:
            "Time Off requests that are pending will appear here for you to approve.",
          testID: TEST_ID,
        }}
        errorState={{
          isError,
          onClick: refetch,
        }}
        loading={isPending}
        sortModel={[
          {
            field: "startDate",
            sort: "asc",
          },
        ]}
        testID={TEST_ID}
        disableColumnMenu
        hideFooterPagination
        disableSelectionOnClick
        hideColumnSelector
        processRowUpdate={handleRowUpdate}
        columnVisibility={isMd ? hiddenColumnsMobile : {}}
      />
    </>
  );
};
